import DashboardCustomizeOutlined from "@mui/icons-material/DashboardCustomizeOutlined";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import EditIcon from "@mui/icons-material/Edit";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import { Grid, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { useCurrentChatStore } from "components/KBo/Stores/useCurrentChat";
import DashboardStore from "components/Signals/Dashboard/DashboardStore";
import {
    ISignalsMontageCard,
    SignalsObjectType,
} from "components/Signals/SignalsModels";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import SoundClipEditor from "components/SoundClipEditor/SoundClipEditor";
import { observer } from "mobx-react";
import SoundClip, { ClipUIModel } from "models/SoundClip";
import AudioContextProvider from "providers/AudioContextProvider";
import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { MontageService } from "services/MontageService";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import { MontageLibraryNavigationURL } from "../../Models/ChatSuggestion";
import { Suggestion } from "../Suggestions/Suggestion";
import { ActionProps } from "./Actions";
import { AddToDashboardDialog } from "./AddToDashboardDialog";

interface PlayMontageArgs {
    montageId: string;
    name: string;
    clipCount: number;
    durationSeconds: number;
}

/**
 * Plays a montage KBo built, with download, Edit in Montage Builder, and Add
 * montage to a dashboard.
 */
export const PlayMontageAction: React.FC<ActionProps> = observer(({ args }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const authStore = useStore(AuthStore);
    const messageStore = useStore(MessageStore);
    const currentChatStore = useCurrentChatStore();
    const [isDownloading, setIsDownloading] = useState(false);
    const [isOpeningBuilder, setIsOpeningBuilder] = useState(false);
    const [isDashboardDialogOpen, setIsDashboardDialogOpen] = useState(false);

    const { montageId, name, clipCount, durationSeconds } = (args ??
        {}) as Partial<PlayMontageArgs>;

    const clip = useMemo(() => {
        const montageClip = new SoundClip() as ClipUIModel;
        montageClip.id = montageId ?? "";
        montageClip.segmentName = name ?? "";
        montageClip.startTime = 0;
        return montageClip;
    }, [montageId, name]);

    // A dashboard shows each montage once
    const isNotOnDashboard = useCallback(
        (dashboard: DashboardStore) =>
            !dashboard.signalsObjects.some(
                (obj) =>
                    obj.objectType === SignalsObjectType.MontageCard &&
                    (obj as ISignalsMontageCard).montageId === montageId,
            ),
        [montageId],
    );

    // Same media and download guards as Montage Library
    const isUltra = authStore.isUserUltra();
    const userCannotViewMedia =
        !isUltra && authStore.canUserView("Restrict Media");
    const canDownload = isUltra || !authStore.canUserView("Restrict Downloads");
    const canEdit = authStore.canUserEdit("Montage Library");
    const canAddToDashboard = authStore.canUserView("Signals");

    if (!montageId || userCannotViewMedia) return null;

    const fileName = name || "MontageFile";

    async function download(id: string) {
        setIsDownloading(true);
        try {
            const blob = await new MontageService().downloadMontage(id);
            new AudioContextProvider().download(blob, `${fileName}.mp3`, false);
        } catch {
            messageStore.logError("Unable to download the montage.");
        } finally {
            setIsDownloading(false);
        }
    }

    async function openInBuilder(id: string) {
        setIsOpeningBuilder(true);
        try {
            await currentChatStore?.prepareEditMontage(id, name ?? "");
            navigate(MontageLibraryNavigationURL);
        } catch {
            messageStore.logError(
                "Unable to open the montage in Montage Builder.",
            );
        } finally {
            setIsOpeningBuilder(false);
        }
    }

    async function addToDashboard(dashboard: DashboardStore) {
        try {
            const added = await dashboard.addCards([
                { type: SignalsObjectType.MontageCard, info: montageId },
            ]);
            if (!added) return false;

            messageStore.logMessage(
                `Added the montage to ${dashboard.title}.`,
                "success",
            );
            // The new card already shows when that dashboard is open
            const dashboardUrl = `/app/signals/dashboard/${dashboard.id}`;
            if (location.pathname !== dashboardUrl) navigate(dashboardUrl);
            return true;
        } catch {
            messageStore.logError(
                "Unable to add the montage to the dashboard.",
            );
            return false;
        }
    }

    const details = [
        durationSeconds != null ? durationDisplay(durationSeconds) : null,
        clipCount ? `${clipCount} clips` : null,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <Grid container direction="column" gap={1} width="100%">
            <Grid
                container
                direction="column"
                gap={1}
                padding={theme.spacing(2)}
                sx={{
                    border: `1px solid ${theme.palette.gray[200]}`,
                    borderRadius: 2,
                    backgroundColor: theme.palette.white.main,
                }}
            >
                <Grid
                    container
                    item
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                >
                    <Grid
                        container
                        item
                        alignItems="center"
                        gap={1}
                        wrap="nowrap"
                        minWidth={0}
                    >
                        <GraphicEqIcon
                            fontSize="small"
                            sx={{ color: theme.palette.primary[500] }}
                        />
                        <Typography fontWeight={600} fontSize="14px" noWrap>
                            {name}
                        </Typography>
                        {details && (
                            <Typography
                                fontSize="13px"
                                color={theme.palette.gray[400]}
                                noWrap
                            >
                                {details}
                            </Typography>
                        )}
                    </Grid>
                    {canDownload && (
                        <Tooltip title="Download">
                            <span>
                                <IconButton
                                    aria-label="Download montage"
                                    size="small"
                                    disabled={isDownloading}
                                    onClick={() => download(montageId)}
                                    sx={{ color: theme.palette.blue.main }}
                                >
                                    <DownloadRounded fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Grid>
                <SoundClipEditor
                    id={`kbo-montage-${montageId}`}
                    noMenu
                    disableClip
                    clip={clip}
                    downloadMediaFileId={montageId}
                    downloadMediaSourceType="montage"
                />
            </Grid>
            {(canEdit || canAddToDashboard) && (
                <Grid container item gap={1}>
                    {canEdit && (
                        <Suggestion
                            startIcon={<EditIcon fontSize="small" />}
                            loading={isOpeningBuilder}
                            onClick={() => openInBuilder(montageId)}
                        >
                            Edit in Montage Builder
                        </Suggestion>
                    )}
                    {canAddToDashboard && (
                        <Suggestion
                            startIcon={
                                <DashboardCustomizeOutlined fontSize="small" />
                            }
                            onClick={() => setIsDashboardDialogOpen(true)}
                        >
                            Add montage to a dashboard
                        </Suggestion>
                    )}
                </Grid>
            )}
            {canAddToDashboard && (
                <AddToDashboardDialog
                    id={`kbo-add-montage-to-dashboard-${montageId}`}
                    isOpen={isDashboardDialogOpen}
                    onClose={() => setIsDashboardDialogOpen(false)}
                    canAddTo={isNotOnDashboard}
                    onAdd={addToDashboard}
                />
            )}
        </Grid>
    );
});
